import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import moment from 'moment';
import * as React from 'react';

import { EnumListKey, useAllEnums } from '~/data/enums';
import { DATE_FORMAT } from '~/utils/helpers/date';

import { CustomFieldInnerProps } from './customFieldModels';
import { CustomFieldType } from './types';

const inputIsEnumerable = (customFieldType: CustomFieldType) => {
  return (
    customFieldType === CustomFieldType.ENUM ||
    customFieldType === CustomFieldType.MULTISELECT
  );
};

export const useCustomField = ({
  formValues,
  fieldDetails,
  updateFormValues,
  setValue,
}: CustomFieldInnerProps) => {
  const { getEnums } = useAllEnums();
  const value = fieldDetails.value;

  const getEnumOptionValue = React.useCallback(
    (group: EnumListKey, code: string) => {
      const option = getEnums(group).find((o) => o.code === code);
      return option ? option.name : code;
    },
    [getEnums],
  );

  const formattedValue: React.ReactNode = React.useMemo(() => {
    if (value === undefined || value === '') {
      return value;
    }

    switch (fieldDetails.inputType.value) {
      case CustomFieldType.BOOLEAN: {
        return value === 'true' ? <CheckBox /> : <CheckBoxOutlineBlank />;
      }
      case CustomFieldType.DATE: {
        return moment(value).format('L');
      }
      case CustomFieldType.ENUM: {
        return getEnumOptionValue(
          fieldDetails.groupName as EnumListKey,
          `${value}`,
        );
      }
      case CustomFieldType.MULTISELECT: {
        if (Array.isArray(value)) {
          return (value as string[])
            .map((code) => {
              return getEnumOptionValue(
                fieldDetails.groupName as EnumListKey,
                code,
              );
            })
            .join(', ');
        } else {
          return getEnumOptionValue(
            fieldDetails.groupName as EnumListKey,
            `${value}`,
          );
        }
      }
      default: {
        return value;
      }
    }
  }, [fieldDetails, value, getEnumOptionValue]);

  const options = React.useMemo(() => {
    if (!inputIsEnumerable(fieldDetails.inputType.value)) {
      return [];
    }

    return getEnums(fieldDetails.groupName as EnumListKey)
      .filter(({ isActive }) => isActive !== false)
      .map(({ name, code }) => ({ label: name, value: code }));
  }, [fieldDetails, getEnums]);
  const inputValue = formValues && formValues[fieldDetails.code];

  const selectedOption = React.useMemo(() => {
    if (!inputIsEnumerable(fieldDetails.inputType.value)) {
      return null;
    }

    return Array.isArray(inputValue)
      ? options.filter((o) => inputValue.includes(o.value))
      : options.find((o) => o.value === inputValue);
  }, [fieldDetails, inputValue, options]);

  const handleChange = React.useCallback(
    (event: unknown, ...otherArgs: unknown[]) => {
      switch (fieldDetails.inputType.value) {
        case CustomFieldType.BOOLEAN: {
          const [checked] = otherArgs as [boolean];

          updateFormValues &&
            updateFormValues({
              [fieldDetails.code]: checked ? 'true' : undefined,
            });

          return;
        }
        case CustomFieldType.DATE: {
          setValue &&
            setValue(
              fieldDetails.code,
              moment(
                //@ts-ignore
                event && (event as moment.Moment).format('L'),
              ).format(DATE_FORMAT),
            );

          return;
        }
        case CustomFieldType.ENUM: {
          setValue &&
            setValue(
              fieldDetails.code,
              (event as { value: string })?.value || '',
            );

          return;
        }
        case CustomFieldType.MULTISELECT: {
          const [value] = otherArgs as [string];

          setValue && setValue(fieldDetails.code, value);

          return;
        }
        default: {
          updateFormValues &&
            updateFormValues({
              [fieldDetails.code]: (
                event as React.ChangeEvent<HTMLInputElement>
              ).target.value,
            });
        }
      }
    },
    [fieldDetails, updateFormValues, setValue],
  );

  const handleClearValue = React.useCallback(() => {
    setValue && setValue(fieldDetails.code, '');
  }, [fieldDetails, setValue]);

  return {
    formattedValue,
    options,
    inputValue,
    selectedOption,
    handleChange,
    handleClearValue,
  };
};
